import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Panel, PanelHeader, Group, Div, Card, Text, Headline, Button,
  Spinner, FormItem, Input, Select,
  ModalRoot, ModalPageHeader, Snackbar,
} from '@vkontakte/vkui';
import { Icon28Notification,
  Icon28CalendarOutline, Icon28UsersOutline, Icon28CheckCircleOutline,
  Icon28WalletOutline, Icon28ServicesOutline, Icon28StatisticsOutline, Icon28ClockOutline,
  Icon28MessageOutline, Icon28UserAddOutline,
  Icon24Cancel,
} from '@vkontakte/icons';
import { useWeddingStore } from '../../stores/wedding';
import type { Wedding } from '../../types';
import { DragHandle } from '../../ui/DragHandle';
import { ResizableModalPage, sheetId } from '../../ui/ResizableModalPage';
import ShareInviteButton from '../../ui/ShareInviteButton';
import CountdownHero from '../../ui/CountdownHero';
import { canCreateWedding } from '../../utils/limits';
import { buildInviteLink, readInviteCodeFromClipboard } from '../../utils/joinCode';
import { api } from '../../api/client';

export default function DashboardPage() {
  const navigate = useNavigate();
  const {
    weddings, currentWedding, loadWeddings, createWedding, joinWedding, selectWedding, updateWedding,
    loadTasks, loadGuests, tasks, guests, isLoading, countdown, loadCountdown,
  } = useWeddingStore();
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editWedding, setEditWedding] = useState<Wedding | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [snack, setSnack] = useState<string | null>(null);
  const [openToken, setOpenToken] = useState(0);
  const [form, setForm] = useState({
    name: '',
    wedding_date: '',
    venue: '',
    budget: '',
    currency: 'RUB',
  });
  const [editForm, setEditForm] = useState({
    name: '',
    partner_name: '',
    wedding_date: '',
    venue: '',
    budget: '',
    currency: 'RUB',
  });
  const [joinCode, setJoinCode] = useState('');
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    loadWeddings();
  }, []);

  // auto-select first wedding (prefer last opened)
  useEffect(() => {
    if (!currentWedding && weddings.length > 0) {
      const saved = localStorage.getItem('colibri.last_wedding_id');
      const target = weddings.find((w) => w.id === saved) || weddings[0];
      selectWedding(target.id);
    }
  }, [weddings, currentWedding, selectWedding]);

  // load real counters
  useEffect(() => {
    if (!currentWedding) return;
    let cancelled = false;
    setStatsLoading(true);
    Promise.allSettled([loadTasks(), loadGuests()]).finally(() => {
      if (!cancelled) setStatsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [currentWedding?.id, loadTasks, loadGuests]);

  async function handleCreate() {
    if (!form.name.trim() || creating) return;
    if (!canCreateWedding(weddings.length)) {
      setSnack('Создание второй свадьбы будет доступно по подписке');
      return;
    }
    setCreating(true);
    try {
      await createWedding({
        name: form.name.trim(),
        wedding_date: form.wedding_date || undefined,
        venue: form.venue.trim() || undefined,
        budget: form.budget ? Number(form.budget) : undefined,
        currency: form.currency,
      });
      setShowCreate(false);
      setForm({ name: '', wedding_date: '', venue: '', budget: '', currency: 'RUB' });
    } catch (e) {
      console.error(e);
    } finally {
      setCreating(false);
    }
  }

  async function handleJoin() {
    if (!joinCode.trim()) return;
    try {
      await joinWedding(joinCode.trim());
      setShowJoin(false);
      setJoinCode('');
      setSnack('Вы присоединились к свадьбе');
      loadWeddings().catch(() => {});
    } catch (e) {
      console.error(e);
      setSnack('Не удалось присоединиться по коду');
    }
  }

  // live countdown: days kept + HH:MM:SS
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // unread notifications → red ringing bell on the «Уведомления» card
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    let alive = true;
    const loadUnread = () => {
      api.get<{ unread: number }>('notifications/unread-count')
        .then((r) => { if (alive) setUnread(r.unread || 0); })
        .catch(() => {});
    };
    loadUnread();
    const t = setInterval(loadUnread, 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  useEffect(() => {
    if (currentWedding?.id) {
      loadCountdown().catch((e) => console.error('loadCountdown', e));
    }
  }, [currentWedding?.id, loadCountdown]);

  const taskTotal = tasks.length;
  const guestTotal = guests.length;

  return (
    <Panel>
      <PanelHeader><span className="cd-header-brand">Colibri</span></PanelHeader>

      {isLoading ? (
        <Div style={{ textAlign: 'center', padding: 40 }}><Spinner size="l" /></Div>
      ) : currentWedding ? (
          <>
            {/* Countdown / wedding card — full-bleed hero, no side gaps */}
            <div className="colibri-hero-full">
              <CountdownHero wedding={currentWedding} countdown={countdown} now={now} compact />
            </div>

            {/* Modules grid — photo texture cards (mockup style), same routes */}
            <Group
              header={
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                  <Headline level="1">Модули</Headline>
                  <Text style={{ color: 'var(--vkui--color_text_secondary)', fontSize: 13, textAlign: 'right' }}>
                    Всё, что нужно для вашей свадьбы →
                  </Text>
                </div>
              }
            >
              <Div style={{ paddingTop: 4 }}>
                <div className="colibri-mod-grid">
                  {[
                    { icon: <Icon28UsersOutline />, label: 'Гости', sub: 'Список, рассадка, приглашения', path: '/guests', tone: 'rose', bg: 1 },
                    { icon: <Icon28CheckCircleOutline />, label: 'Задачи', sub: 'Планирование и контроль', path: '/tasks', tone: 'mint', bg: 2 },
                    { icon: <Icon28WalletOutline />, label: 'Бюджет', sub: 'Расходы и доходы', path: '/budget', tone: 'sand', bg: 3 },
                    { icon: <Icon28ServicesOutline />, label: 'Подрядчики', sub: 'Выбор и контакты', path: '/vendors', tone: 'lilac', bg: 4 },
                    { icon: <Icon28CalendarOutline />, label: 'Мероприятия', sub: 'День свадьбы и подготовка', path: '/events', tone: 'sky', bg: 5 },
                    { icon: <Icon28UserAddOutline />, label: 'Рассадка', sub: 'Столы, меню и гости', path: '/seating', tone: 'pink', bg: 6 },
                    { icon: <Icon28ClockOutline />, label: 'Отсчёт', sub: 'До главного дня', path: '/countdown', tone: 'peach', bg: 7 },
                    { icon: <Icon28MessageOutline />, label: 'Чат', sub: 'Общение и поддержка', path: '/messages', tone: 'aqua', bg: 8 },
                    { icon: <Icon28UserAddOutline />, label: 'Участники', sub: 'Ваша команда', path: '/members', tone: 'peach', bg: 9 },
                    { icon: <Icon28Notification />, label: 'Уведомления', sub: 'Напоминания и события', path: '/notifications', tone: 'sky', bg: 10 },
                  ].map((item) => (
                    <div
                      key={item.path}
                      className={`colibri-mod-card colibri-mod-card--${item.tone}`}
                      onClick={() => navigate(item.path)}
                    >
                      <div
                        className="colibri-mod-card__bg"
                        style={{ backgroundImage: `url(/modules/card-${item.bg}.jpg)` }}
                      />
                      <div className="colibri-mod-card__veil" />
                      <div className="colibri-mod-card__icon">
                        {item.path === '/notifications' && unread > 0 ? (
                          <span className="colibri-bell-alert">
                            <Icon28Notification fill="var(--colibri-danger)" />
                          </span>
                        ) : item.icon}
                      </div>
                      <div className="colibri-mod-card__body">
                        <div className="colibri-mod-card__title">{item.label}</div>
                        <div className="colibri-mod-card__sub">{item.sub}</div>
                      </div>
                      <div className="colibri-mod-card__arrow">→</div>
                    </div>
                  ))}
                </div>
              </Div>
            </Group>

            {/* Summary tiles (mockup style) */}
            <Group
              header={
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                  <Headline level="1">Сводка</Headline>
                  <Text
                    style={{ color: '#c45b7a', fontSize: 13, cursor: 'pointer' }}
                    onClick={() => navigate('/analytics')}
                  >
                    Подробнее →
                  </Text>
                </div>
              }
            >
              {statsLoading ? (
                <Div style={{ textAlign: 'center', padding: 16 }}><Spinner size="m" /></Div>
              ) : (
                <Div style={{ paddingTop: 4 }}>
                  <div className="colibri-sum-grid">
                    <div className="colibri-sum-tile colibri-sum-tile--blue" onClick={() => navigate('/tasks')}>
                      <div className="colibri-sum-tile__icon"><Icon28CheckCircleOutline width={22} height={22} /></div>
                      <div>
                        <div className="colibri-sum-tile__label">Задачи</div>
                        <div className="colibri-sum-tile__value">{taskTotal}</div>
                      </div>
                    </div>
                    <div className="colibri-sum-tile colibri-sum-tile--violet" onClick={() => navigate('/guests')}>
                      <div className="colibri-sum-tile__icon"><Icon28UsersOutline width={22} height={22} /></div>
                      <div>
                        <div className="colibri-sum-tile__label">Гости</div>
                        <div className="colibri-sum-tile__value">{guestTotal}</div>
                      </div>
                    </div>
                    <div className="colibri-sum-tile colibri-sum-tile--rose" onClick={() => navigate('/budget')}>
                      <div className="colibri-sum-tile__icon"><Icon28WalletOutline width={22} height={22} /></div>
                      <div>
                        <div className="colibri-sum-tile__label">Бюджет</div>
                        <div className="colibri-sum-tile__value">
                          {Number(currentWedding.budget || 0).toLocaleString('ru-RU')} {currentWedding.currency}
                        </div>
                      </div>
                    </div>
                    <div className="colibri-sum-tile colibri-sum-tile--sand" onClick={() => navigate('/analytics')}>
                      <div className="colibri-sum-tile__icon"><Icon28StatisticsOutline width={22} height={22} /></div>
                      <div>
                        <div className="colibri-sum-tile__label">Аналитика</div>
                        <div className="colibri-sum-tile__value">→</div>
                      </div>
                    </div>
                  </div>
                </Div>
              )}
            </Group>

            {/* Invite */}
            <Group header={<Text>Совместный доступ</Text>}>
              <Card style={{ margin: 12, padding: 14 }}>
                <Headline level="2">Пригласить участника</Headline>
                <Text style={{ marginTop: 6, fontSize: 13, color: 'var(--vkui--color_text_secondary)' }}>
                  Код: <strong>{currentWedding.invite_code}</strong>
                </Text>
                <Div style={{ padding: 0, marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <ShareInviteButton
                    size="m"
                    invite={{
                      code: currentWedding.invite_code,
                      link: buildInviteLink(currentWedding.invite_code),
                      title: currentWedding.name,
                    }}
                  />
                  <Button
                    size="m"
                    mode="secondary"
                    onClick={() => navigate('/members')}
                  >
                    Участники и права
                  </Button>
                </Div>
                <Button
                  size="m"
                  mode="outline"
                  stretched
                  style={{ marginTop: 10 }}
                  onClick={() => setShowJoin(true)}
                >
                  Вступить в другую свадьбу по коду
                </Button>
              </Card>
            </Group>
          </>
        ) : (
          <Group>
            <Div style={{ textAlign: 'center', padding: 40 }}>
              <Headline>Нет активной свадьбы</Headline>
              <Text style={{ margin: '12px 0 20px' }}>Создайте новую свадьбу или присоединитесь по коду</Text>
              <Button size="l" mode="primary" stretched onClick={() => setShowCreate(true)}>Создать свадьбу</Button>
              <Button size="l" mode="secondary" stretched style={{ marginTop: 8 }} onClick={() => setShowJoin(true)}>Присоединиться по коду</Button>
            </Div>

            {showCreate && <CreateForm form={form} setForm={setForm} creating={creating} onSubmit={handleCreate} onCancel={() => setShowCreate(false)} />}
          </Group>
        )
      }

      {showJoin && (
        <Group>
          <Div>
            <FormItem top="Код приглашения">
              <Input value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} placeholder="Например: 0N2K6HNU" autoCapitalize="characters" />
            </FormItem>
            <Button
              size="m"
              mode="secondary"
              style={{ marginBottom: 8 }}
              onClick={async () => {
                const code = await readInviteCodeFromClipboard();
                if (code) setJoinCode(code);
                else setSnack('Не удалось прочитать буфер — вставьте код вручную');
              }}
            >
              Вставить из буфера
            </Button>
            <Button size="l" mode="primary" stretched onClick={handleJoin}>Присоединиться</Button>
            <Button size="l" mode="secondary" stretched style={{ marginTop: 8 }} onClick={() => setShowJoin(false)}>Отмена</Button>
          </Div>
        </Group>
      )}

      <ModalRoot activeModal={editWedding ? sheetId('wedding-edit', openToken) : null} onClose={() => setEditWedding(null)}>
        <ResizableModalPage openToken={openToken} id="wedding-edit" onClose={() => setEditWedding(null)} settlingHeight={80}>
          <ModalPageHeader>Редактировать свадьбу</ModalPageHeader>
        <DragHandle />
          <Group>
            <FormItem top="Название *">
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="Свадьба Анны и Дмитрия"
              />
            </FormItem>
            <FormItem top="Партнёр">
              <Input
                value={editForm.partner_name}
                onChange={(e) => setEditForm({ ...editForm, partner_name: e.target.value })}
              />
            </FormItem>
            <FormItem top="Дата свадьбы">
              <Input
                type="date"
                value={editForm.wedding_date}
                onChange={(e) => setEditForm({ ...editForm, wedding_date: e.target.value })}
              />
            </FormItem>
            <Text style={{ padding: '0 16px 8px', fontSize: 12, color: 'var(--vkui--color_text_secondary)' }}>
              При смене даты пересчитаются сроки задач (кроме изменённых вручную).
            </Text>
            <FormItem top="Место">
              <Input
                value={editForm.venue}
                onChange={(e) => setEditForm({ ...editForm, venue: e.target.value })}
              />
            </FormItem>
            <FormItem top="Бюджет">
              <Input
                type="number"
                inputMode="decimal"
                value={editForm.budget}
                onChange={(e) => setEditForm({ ...editForm, budget: e.target.value })}
              />
            </FormItem>
            <FormItem top="Валюта">
              <Select
                value={editForm.currency}
                onChange={(e) => setEditForm({ ...editForm, currency: e.target.value })}
                options={[
                  { label: 'RUB — ₽', value: 'RUB' },
                  { label: 'USD — $', value: 'USD' },
                  { label: 'EUR — €', value: 'EUR' },
                  { label: 'KZT — ₸', value: 'KZT' },
                  { label: 'BYN — Br', value: 'BYN' },
                ]}
              />
            </FormItem>
            <Div>
              <Button
                size="l"
                mode="primary"
                stretched
                disabled={editSaving || !editForm.name.trim()}
                onClick={async () => {
                  if (!editWedding) return;
                  setEditSaving(true);
                  try {
                    await updateWedding(editWedding.id, {
                      name: editForm.name.trim(),
                      partner_name: editForm.partner_name.trim() || undefined,
                      wedding_date: editForm.wedding_date || undefined,
                      venue: editForm.venue.trim() || undefined,
                      budget: editForm.budget ? Number(editForm.budget) : undefined,
                      currency: editForm.currency,
                    });
                    await loadWeddings();
                    setOpenToken((t) => t + 1);
                    setEditWedding(null);
                    setSnack('Свадьба сохранена');
                  } catch {
                    setSnack('Не удалось сохранить');
                  } finally {
                    setEditSaving(false);
                  }
                }}
              >
                {editSaving ? 'Сохранение…' : 'Сохранить'}
              </Button>
              <Button size="l" mode="secondary" stretched style={{ marginTop: 8 }} onClick={() => setEditWedding(null)}>
                Отмена
              </Button>
            </Div>
          </Group>
        </ResizableModalPage>
      </ModalRoot>

      {snack && (
        <Snackbar onClose={() => setSnack(null)} onClosed={() => setSnack(null)} before={<Icon24Cancel />}>
          {snack}
        </Snackbar>
      )}
    </Panel>
  );
}

function CreateForm({
  form, setForm, creating, onSubmit, onCancel,
}: {
  form: { name: string; wedding_date: string; venue: string; budget: string; currency: string };
  setForm: (f: { name: string; wedding_date: string; venue: string; budget: string; currency: string }) => void;
  creating: boolean;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  return (
    <Div>
      <FormItem top="Название свадьбы">
        <Input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Например: Свадьба Анны и Дмитрия"
        />
      </FormItem>
      <FormItem top="Дата свадьбы">
        <Input type="date" value={form.wedding_date} onChange={(e) => setForm({ ...form, wedding_date: e.target.value })} />
      </FormItem>
      <FormItem top="Место">
        <Input
          value={form.venue}
          onChange={(e) => setForm({ ...form, venue: e.target.value })}
          placeholder="Ресторан / адрес"
        />
      </FormItem>
      <FormItem top="Бюджет">
        <Input
          type="number"
          value={form.budget}
          onChange={(e) => setForm({ ...form, budget: e.target.value })}
          placeholder="Например: 500000"
        />
      </FormItem>
      <FormItem top="Валюта">
        <Select
          value={form.currency}
          onChange={(e) => setForm({ ...form, currency: e.target.value })}
          options={[
            { label: 'RUB — рубль', value: 'RUB' },
            { label: 'USD — доллар', value: 'USD' },
            { label: 'EUR — евро', value: 'EUR' },
            { label: 'KZT — тенге', value: 'KZT' },
            { label: 'BYN — рубль BY', value: 'BYN' },
          ]}
        />
      </FormItem>
      <Button size="l" mode="primary" stretched onClick={onSubmit} disabled={creating || !form.name.trim()}>
        {creating ? 'Создание…' : 'Создать'}
      </Button>
      <Button size="l" mode="secondary" stretched style={{ marginTop: 8 }} onClick={onCancel}>Отмена</Button>
    </Div>
  );
}
